/* eslint-disable sort-keys-fix/sort-keys-fix */
import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import TablePagination from '@mui/material/TablePagination';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import DialogExplicitConfirmation from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';

import * as R from 'ramda';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import { useUserRolePermissions } from 'hooks';
import { capitalizeSingleWord } from 'utils/helpers/format';
import { useDispatch } from 'react-redux';
import { createSnack } from 'snackbar/snackbarSlice';
import { DataLoadingCard, NoAvailableCard } from 'common';
import { Badge } from '@mui/material';
import { useCancelReconsignmentMutation } from '../reconsignmentsEndpoints';
import { useDeleteReconsignmentMutation } from '../autoReconsignmentsEndpoints';
import ReconsignmentCard from './ReconsignmentCard';
import AutoReconsignmentCard from './AutoReconsignmentCard';
import Header from '../header/HeaderConnector';

const mobileToolbarFix = {
  '@media (max-width:1050px)': {
    '& .MuiDataGrid-toolbarContainer': {
      height: 'auto !important',
      flexWrap: 'wrap !important',
      rowGap: 1,
    },
    '& .MuiDataGrid-toolbarContainer > .MuiBox-root': {
      order: 1,
      width: '100% !important',
      justifyContent: 'flex-start',
      mb: 1.5,
    },
  },
};
const MainPanel = ({
  autoReconsignments,
  confirmDialogType,
  filterCount,
  definedValues,
  isFiltersOpen,
  loading,
  pageNumber,
  reconsignments,
  resultsPerPage,
  selectedReconsignment,
  setConfirmDialogType,
  setIsFiltersOpen,
  setPageNumber,
  setResultsPerPage,
  setSelectedReconsignment,
  setTab,
  tab,
  totalResults,
  setModalMode,
  handleCopyReconsignment,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const dispatch = useDispatch();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const warningList = useMemo(
    () => [
      `Are you sure you want to ${confirmDialogType} this reconsignment? This process cannot be undone.`,
    ],
    [confirmDialogType]
  );

  const message = `If you still want to ${confirmDialogType} the reconsignment, enter the word below and click Confirm.`;

  const { currentAffiliate } = useUserRolePermissions();
  const body = {
    inventoryOwner: {
      companyCustomerIdName: R.prop('customerIdName', currentAffiliate),
      dataCompanyId: R.prop('dataCompanyId', currentAffiliate),
    },
    reconsignmentId: selectedReconsignment,
  };

  const [cancel] = useCancelReconsignmentMutation();

  const cancelReconsignment = () => {
    cancel({ body })
      .then(res => {
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: res.data.message,
            })
          );
          setIsDialogOpen(false);
        }
      })
      .finally(() =>
        setTimeout(() => {
          setSelectedReconsignment(null);
          setConfirmDialogType('');
        }, 500)
      );
  };

  const [remove] = useDeleteReconsignmentMutation();

  const deleteReconsignment = () => {
    remove({ body })
      .then(res => {
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: res.data.message,
            })
          );
          setIsDialogOpen(false);
        }
      })
      .finally(() =>
        setTimeout(() => {
          setSelectedReconsignment(null);
          setConfirmDialogType('');
        }, 500)
      );
  };

  const renderSkeleton = R.always(<DataLoadingCard type="data" />);

  const renderReconsignment = R.cond([
    [R.isEmpty, R.always(<NoAvailableCard type="reconsignments" />)],
    [
      R.T,
      R.always(
        <ReconsignmentCard
          loading={loading}
          reconsignments={reconsignments}
          setConfirmDialogType={setConfirmDialogType}
          setIsDialogOpen={setIsDialogOpen}
          setModalMode={setModalMode}
          setSelectedReconsignment={setSelectedReconsignment}
        />
      ),
    ],
  ]);

  const renderAutoReconsignment = R.cond([
    [R.isEmpty, R.always(<NoAvailableCard type="auto reconsignments" />)],
    [
      R.T,
      R.always(
        <AutoReconsignmentCard
          autoReconsignments={autoReconsignments}
          handleCopyReconsignment={handleCopyReconsignment}
          setConfirmDialogType={setConfirmDialogType}
          setIsDialogOpen={setIsDialogOpen}
          setModalMode={setModalMode}
          setSelectedReconsignment={setSelectedReconsignment}
        />
      ),
    ],
  ]);

  const urlPrefix = R.ifElse(
    R.always(R.equals(tab, 'manual')),
    R.always('reconsignments'),
    R.always('auto-reconsignments')
  )();

  return (
    <>
      <TabContext value={tab}>
        <Box
          sx={{
            backgroundColor: 'surface.light',
            left: 0,
            pt: 3,
            px: { xs: 2, lg: 3 },
            top: { xs: 112, lg: 48 },
            overflow: 'hidden',
            width: '100%',
            zIndex: 1,
          }}
        >
          <Header definedValues={definedValues} tab={tab} urlPrefix={urlPrefix} />
          <Button
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            startIcon={<FilterListOutlinedIcon sx={{ color: 'primary.dark', mt: '-2px' }} />}
            sx={{
              borderColor: 'gray.main',
              borderRadius: 0.5,
              color: 'text.primary',
              display: { xs: 'none', lg: 'inline-flex' },
              height: 48,
              my: 2,
            }}
            variant="outlined"
          >
            <span display={{ xs: 'none', md: 'inherit' }}>Show Filters</span>
            {!!filterCount && (
              <Badge
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'center',
                }}
                badgeContent={filterCount}
                color="primary"
                sx={{
                  width: '20px',
                  height: '20px',
                  marginLeft: '8px',
                  marginRight: '-4px',
                  marginBottom: '2px',
                }}
              />
            )}
          </Button>
          <Box
            sx={{
              backgroundColor: 'surface.light',
              borderBottom: 1,
              borderColor: 'divider',
              bottom: 0,
              boxShadow: { lg: 'none', xs: 1 },
              left: 0,
              mt: { lg: 1, xs: 1 },
              position: 'relative',
              width: 1,
              zIndex: 1,
            }}
          >
            <TabList
              onChange={(_, val) => {
                setTab(val);
                window.scrollTo(0, 0);
              }}
              sx={{
                width: 108,
              }}
            >
              <Tab label="Manual" sx={{ minWidth: 20, px: 0, py: 1 }} value="manual" />
              <Tab label="Auto" sx={{ minWidth: 20, px: 0, py: 1 }} value="auto" />
            </TabList>
          </Box>
        </Box>
        <TabPanel
          sx={{
            minHeight: 400,
            height: () => ({ lg: 'calc(100vh - 280px)', xs: 'calc(100vh - 344px)' }),
            pt: 0,
            ...mobileToolbarFix,
          }}
          value="manual"
        >
          {loading ? renderSkeleton() : renderReconsignment(reconsignments)}
        </TabPanel>
        <TabPanel
          sx={{
            minHeight: 400,
            height: () => ({ lg: 'calc(100vh - 280px)', xs: 'calc(100vh - 344px)' }),
            pt: 0,
            ...mobileToolbarFix,
          }}
          value="auto"
        >
          {loading ? renderSkeleton() : renderAutoReconsignment(autoReconsignments)}
        </TabPanel>
      </TabContext>
      {R.or(
        R.and(R.equals('manual', tab), R.lte(1, R.length(reconsignments))),
        R.and(R.equals('auto', tab), R.lte(1, R.length(autoReconsignments)))
      ) && (
        <TablePagination
          component="div"
          count={totalResults}
          labelRowsPerPage={lgUp ? 'Rows per page' : ''}
          onPageChange={(_, newPage) => {
            setPageNumber(newPage);
            window.scrollTo(0, 0);
          }}
          onRowsPerPageChange={event => {
            setResultsPerPage(event.target.value);
            setPageNumber(0);
            window.scrollTo(0, 0);
          }}
          page={pageNumber}
          rowsPerPage={resultsPerPage}
          rowsPerPageOptions={[25, 50, 75, 100]}
          showFirstButton
          showLastButton
          sx={{
            '.MuiInputBase-root': {
              margin: theme => ({
                /* eslint-disable sort-keys-fix/sort-keys-fix */
                xs: 0,
                lg: theme.spacing(0, 4, 0, 2),
                /* eslint-enable sort-keys-fix/sort-keys-fix */
              }),
            },
            '.MuiTablePagination-selectLabel': {
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              display: { xs: 'none', lg: 'unset' },
            },
            '.MuiTablePagination-spacer': {
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              display: { xs: 'none', lg: 'unset' },
            },
            '.MuiTablePagination-toolbar': {
              '.MuiTablePagination-actions': {
                '& .MuiButtonBase-root:not([disabled])': {
                  color: 'primary.main',
                },
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                marginLeft: { xs: 0, lg: 2 },
              },
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              justifyContent: { xs: 'space-between', lg: 'unset' },
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              margin: { xs: '0 0 0 auto', lg: 'unset' },
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              maxWidth: { xs: 400, lg: 'unset' },
              padding: theme => theme.spacing(0, 2),
            },
            '@media (max-width:1050px)': {
              position: 'static',
            },
            backgroundColor: 'surface.light',
            bottom: 0,
            justifyContent: 'space-between',
            position: 'fixed',
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            px: { xs: 2, lg: 'unset' },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            py: { xs: 0, lg: 'unset' },
            right: 0,
            width: 1,
            zIndex: 10,
          }}
        />
      )}
      <DialogExplicitConfirmation
        confirmationMessage={message}
        confirmationValue={confirmDialogType.toUpperCase()}
        dialogOpen={isDialogOpen}
        handleCloseDialog={() => {
          setIsDialogOpen(false);
          setTimeout(() => {
            setConfirmDialogType('');
            setSelectedReconsignment(null);
          }, 600);
        }}
        label={`Type "${confirmDialogType.toUpperCase()}"`}
        onConfirm={confirmDialogType === 'delete' ? deleteReconsignment : cancelReconsignment}
        title={`${capitalizeSingleWord(confirmDialogType)} Reconsignment?`}
        warningList={warningList}
      />
    </>
  );
};

MainPanel.propTypes = {
  autoReconsignments: arrayOf(shape({})).isRequired,
  confirmDialogType: string.isRequired,
  definedValues: shape({}).isRequired,
  filterCount: number,
  handleCopyReconsignment: func.isRequired,
  isFiltersOpen: bool.isRequired,
  loading: bool.isRequired,
  pageNumber: number.isRequired,
  reconsignments: arrayOf(shape({})).isRequired,
  resultsPerPage: number.isRequired,
  selectedReconsignment: number,
  setConfirmDialogType: func.isRequired,
  setIsFiltersOpen: func.isRequired,
  setModalMode: func.isRequired,
  setPageNumber: func.isRequired,
  setResultsPerPage: func.isRequired,
  setSelectedReconsignment: func.isRequired,
  setTab: func.isRequired,
  tab: string.isRequired,
  totalResults: number.isRequired,
};

MainPanel.defaultProps = {
  filterCount: 0,
  selectedReconsignment: null,
};

export default MainPanel;
